import { AHSeparator, AHText, Box, ListItem } from "arkh";
import { useLazyLoadQuery, graphql } from "react-relay";
import { FlatList } from "react-native";

import React, { useCallback, useMemo, useState } from "react";
import { LazyQueryNetworkBoundrary } from "../../../../components/mobile/QueryNetworkBoundrary";
import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";

import { OrgSearchInput } from "../../OrgSearchInput";
import { WorkspaceEmptyListItem } from "../../WorkspaceEmptyListItem";
import { listGutterStyle } from "../../guterStyle";
import { useOrgHomeDetailLayout } from "../../useOrgHomeDetailContainerStyle";
import { OrgAdminScreenProps } from "../OrgAdmin";
import { OrgButton } from "../../OrgButton";
import { OrgAdminTeamsScreenQuery } from "./__generated__/OrgAdminTeamsScreenQuery.graphql";
import { distinct } from "@/src/utils";
import { OrgTeamListItem } from "./OrgTeamListItem";

function InnerOrgTeamsScreen(
  props: OrgAdminScreenProps<"OrgAdminTeams"> & { fetchKey: string }
) {
  const [search, setSearch] = useState("");
  const data = useLazyLoadQuery<OrgAdminTeamsScreenQuery>(
    graphql`
      query OrgAdminTeamsScreenQuery {
        organisation @required(action: THROW) {
          id
          name
          teams(first: 1000) {
            edges {
              node {
                name
                id
                ...OrgTeamListItem_team
              }
            }
          }
        }
      }
    `,
    {},
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);

  const allTeams = useMemo(() => {
    return (
      data.organisation.teams?.edges
        .map((e) => e?.node)
        .sort((a, b) =>
          a.name.toLocaleLowerCase() > b.name.toLocaleLowerCase() ? 1 : -1
        ) ?? []
    );
  }, [data.organisation.teams]);
  const listData = useMemo(() => {
    if (search.length > 0) {
      const normalizedSearch = search.toLocaleLowerCase();
      return allTeams.filter((s) =>
        s.name.toLocaleLowerCase().includes(normalizedSearch)
      );
    }
    return allTeams;
  }, [allTeams, search]);

  const renderItem = useCallback(
    ({ item, index }: { item: (typeof listData)[number]; index: number }) => {
      const isSelected = selectedTeams.includes(item.id);
      return (
        <OrgTeamListItem
          isSelected={isSelected}
          onToggleSelect={() => {
            if (isSelected) {
              setSelectedTeams(selectedTeams.filter((t) => t !== item.id));
            } else {
              setSelectedTeams([...selectedTeams, item.id]);
            }
          }}
          team={item}
          isLast={index === listData.length - 1}
          onPress={() => {
            props.navigation.navigateDeprecated("TeamAdmin", {
              screen: "TeamAdminIdentifiers",
              params: {
                teamId: item.id,
              },
            } as any);
          }}
        />
      );
    },
    [listData.length, props.navigation, selectedTeams]
  );

  const detailLayout = useOrgHomeDetailLayout();
  const intl_tokens = useIntlTokens();

  const isAllVisableSelected = useMemo(() => {
    return listData.every((ld) => selectedTeams.includes(ld.id));
  }, [selectedTeams, listData]);

  const checkboxStatus = useMemo(() => {
    if (selectedTeams.length === 0) {
      return false;
    }

    if (selectedTeams.length === allTeams.length) {
      return true;
    }
    return "indeterminate";
  }, [selectedTeams.length, allTeams.length]);

  function onToggleSelect() {
    if (isAllVisableSelected) {
      setSelectedTeams((prev) =>
        prev.filter((t) => !listData.map((ld) => ld.id).includes(t))
      );
    } else {
      setSelectedTeams((prev) =>
        distinct([...prev, ...listData.map((t) => t.id)])
      );
    }
  }
  return (
    <React.Fragment>
      <FlatList
        style={[
          {
            flex: 1,
            marginBottom: detailLayout.keyboardBottomInset,
          },
          listGutterStyle,
        ]}
        contentContainerStyle={[
          detailLayout.contentContainerStyle,
          { minWidth: 300 },
        ]}
        data={listData}
        keyboardShouldPersistTaps="handled"
        renderItem={renderItem}
        keyExtractor={(item) => item.id}
        ItemSeparatorComponent={AHSeparator}
        ListEmptyComponent={WorkspaceEmptyListItem}
        stickyHeaderIndices={[0]}
        ListHeaderComponent={
          <Box color="surface-background-light" spaceTop="medium">
            <OrgSearchInput
              placeholder={intl_tokens.Search}
              value={search}
              onChangeText={setSearch}
            />
            <Box color="surface-background-light">
              <ListItem
                displayMode="TABLE"
                border="border-primary"
                onPress={() => {
                  onToggleSelect();
                }}
                left={
                  <ListItem.MultiSelectIndicator selected={checkboxStatus} />
                }
              >
                <Box
                  style={{
                    flexDirection: "row",
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <AHText type="subtitle">{intl_tokens.Name}</AHText>
                </Box>
              </ListItem>
              <AHSeparator />
            </Box>
          </Box>
        }
      />
      <AHSeparator />
      <Box
        color="surface"
        space="medium"
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
        gap="medium"
      >
        <OrgButton
          title={intl_tokens.CreateTeam}
          onPress={() => {
            props.navigation.navigateDeprecated("OrgAdminCreateTeam", {});
          }}
          backgroundColor="primary"
          tintColor="text-inverted"
          icon="add"
        />
      </Box>
    </React.Fragment>
  );
}

export function OrgAdminTeamsScreen(
  props: OrgAdminScreenProps<"OrgAdminTeams">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey: string) => <InnerOrgTeamsScreen {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}
